import { useEffect, useState } from "react";
import { VGTIcon, type LieferscheinArgs, type ProjektOption } from "../lib/vgt";
import { ProjektAuswahl } from "./ProjektAuswahl";

/** Woher der Lieferschein kommt: aus einem Einsatz heraus steht die Baustelle
 *  schon fest, vom Geld-Reiter aus sucht der Helfer sie selbst. */
export type LieferscheinZiel = { projektId?: string };

const TYPEN = ["application/pdf", "image/jpeg", "image/jpg", "image/png"];
const MAX = 20 * 1024 * 1024;
const MAX_SEITEN = 12;

/** Lieferschein abgeben — Fotos oder ein PDF, dazu die Baustelle(n).
 *
 *  Mehrere Projekte sind Absicht: eine Lieferung deckt oft zwei Baustellen ab,
 *  und wer sich dann für eine entscheiden muss, rät. Die Aufteilung der Kosten
 *  macht danach das Büro — hier wird nur festgehalten, wohin das Material ging.
 *  Betrag und Lieferant fragt die App nicht ab: das steht auf dem Blatt und
 *  wird im Büro ausgelesen. */
export function LieferscheinSheet({ projekte, initialProjektId, onClose, onSubmit, busy }: {
  projekte: ProjektOption[];
  initialProjektId?: string;
  onClose: () => void;
  onSubmit: (args: LieferscheinArgs) => void;
  busy: boolean;
}) {
  const [dateien, setDateien] = useState<File[]>([]);
  const [pids, setPids] = useState<string[]>(initialProjektId ? [initialProjektId] : []);
  const [wahl, setWahl] = useState("");
  const [notiz, setNotiz] = useState("");
  const [err, setErr] = useState("");
  const [drag, setDrag] = useState(false);

  // Kommt der Helfer aus einem Einsatz, ist das Projekt vorbelegt — aber erst,
  // wenn die Liste geladen ist, sonst steht eine ID ohne Namen im Feld.
  useEffect(() => {
    if (initialProjektId && !pids.includes(initialProjektId)
        && projekte.some(p => p.id === initialProjektId)) {
      setPids(alt => [...alt, initialProjektId]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialProjektId, projekte]);

  const titel = (id: string) => projekte.find(p => p.id === id)?.title || "Projekt";
  const ohneMich = projekte.filter(p => !pids.includes(p.id));

  const annehmen = (liste: FileList | null) => {
    if (!liste || liste.length === 0) return;
    const neu = [...dateien];
    for (const f of Array.from(liste)) {
      if (!TYPEN.includes(f.type)) { setErr("Nur PDF, JPG oder PNG."); return; }
      if (f.size > MAX) { setErr("Datei zu groß (max. 20 MB)."); return; }
      neu.push(f);
    }
    if (neu.some(f => f.type === "application/pdf") && neu.length > 1) {
      setErr("Entweder ein PDF oder mehrere Fotos — nicht gemischt."); return;
    }
    if (neu.length > MAX_SEITEN) { setErr(`Höchstens ${MAX_SEITEN} Seiten.`); return; }
    setErr(""); setDateien(neu);
  };

  const fmtSize = (b: number) => b < 1024 * 1024 ? Math.round(b / 1024) + " KB" : (b / 1048576).toFixed(1) + " MB";

  return (
    <div className="vgt-sheet-back" onClick={onClose}>
      <div className="vgt-sheet" onClick={e => e.stopPropagation()}>
        <div className="vgt-sheet-grab" />
        <div className="vgt-sheet-title">Lieferschein abgeben</div>
        <div className="vgt-sheet-sub">
          Fotografier den Lieferschein und sag uns, für welche Baustelle das Material war.
          Wir lesen ihn im Büro aus — Lieferant und Positionen musst du nicht eintippen.
        </div>

        <div className="vgt-field-label">Baustelle{pids.length > 1 ? "n" : ""}</div>
        {pids.length > 0 && (
          <div className="vgt-chips">
            {pids.map(id => (
              <button key={id} type="button" className="vgt-chip is-entf"
                      onClick={() => setPids(alt => alt.filter(x => x !== id))}>
                {titel(id)} <VGTIcon name="minus" />
              </button>
            ))}
          </div>
        )}
        {projekte.length === 0
          ? <div className="vgt-hinweis"><span className="pin"><VGTIcon name="info" /></span><div>Gerade läuft keine Baustelle, der wir das zuordnen könnten. Gib uns den Schein direkt.</div></div>
          : (
            <ProjektAuswahl
              projekte={ohneMich}
              value={wahl}
              platzhalter={pids.length ? "Noch eine Baustelle …" : "Baustelle suchen …"}
              leerText="Nichts gefunden. Wähl eine laufende Baustelle."
              onChange={(id) => { if (id) { setPids(alt => [...alt, id]); setWahl(""); } }}
            />
          )}

        <label
          className={"vgt-drop" + (drag ? " is-drag" : "") + (dateien.length ? " is-set" : "")}
          onDragOver={e => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={e => { e.preventDefault(); setDrag(false); annehmen(e.dataTransfer.files); }}
        >
          <input type="file" multiple accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png" hidden
                 onChange={e => { annehmen(e.target.files); e.target.value = ""; }} />
          {dateien.length > 0 ? (
            <div className="vgt-drop-file">
              <span className="ic"><VGTIcon name="doc" /></span>
              <div className="m">
                <div className="n">{dateien.length === 1 ? dateien[0].name : `${dateien.length} Seiten`}</div>
                <div className="s">{fmtSize(dateien.reduce((sum, f) => sum + f.size, 0))} · tippen für mehr</div>
              </div>
            </div>
          ) : (
            <div className="vgt-drop-empty">
              <span className="ic"><VGTIcon name="arrowup" /></span>
              <div className="t">Lieferschein fotografieren<br /><span className="sub">oder tippen zum Auswählen · PDF, JPG, PNG</span></div>
            </div>
          )}
        </label>
        {dateien.length > 1 && (
          <button className="vgt-linkbtn" type="button" onClick={() => setDateien([])}>
            <VGTIcon name="minus" /> Seiten verwerfen
          </button>
        )}
        {err && <div className="vgt-login-err" style={{ marginTop: 8 }}>{err}</div>}

        <div>
          <div className="vgt-field-label">Notiz (optional)</div>
          <textarea className="vgt-textarea" value={notiz} onChange={e => setNotiz(e.target.value)}
                    placeholder="z. B. Teillieferung, Rest kommt noch" />
        </div>

        <div className="vgt-actions">
          <button className="vgt-cta is-soft" onClick={onClose}>Abbrechen</button>
          <button className="vgt-cta" disabled={busy || dateien.length === 0 || pids.length === 0}
                  onClick={() => onSubmit({ dateien, projektIds: pids, notiz: notiz.trim() || undefined })}>
            {busy ? "Lädt hoch …" : <><VGTIcon name="arrowup" /> Abgeben</>}
          </button>
        </div>
      </div>
    </div>
  );
}
